import { InfoIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DimensionType,
  DIMENSION_LABELS,
  DimensionGroupingConfig,
  createDefaultCatchAllGroups,
} from '@/types/rebateAnalytics';
import { DimensionGroupingEditor } from './DimensionGroupingEditor';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';

interface DimensionGroupingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dimensionType: Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  > | null;
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >;
  onDimensionGroupingConfigChange?: (
    dimensionType: Exclude<
      DimensionType,
      DimensionType.TIME | DimensionType.RULE_TYPE
    >,
    config: DimensionGroupingConfig | undefined,
  ) => void;
  // Maps for different dimension types
  supplierIdToName?: Map<string, string>;
  divisionIdToName?: Map<string, string>;
  ruleIdToName?: Map<string, string>;
  agreementIdToName?: Map<string, string>;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
}

export function DimensionGroupingModal({
  open,
  onOpenChange,
  dimensionType,
  dimensionGroupingConfig,
  onDimensionGroupingConfigChange,
  supplierIdToName,
  divisionIdToName,
  ruleIdToName,
  agreementIdToName,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
}: DimensionGroupingModalProps) {
  if (!dimensionType) {
    return null;
  }

  // Get the appropriate itemIdToName map based on dimension type
  let itemIdToName: Map<string, string> | undefined;
  let groupingConfig: DimensionGroupingConfig | undefined;

  if (dimensionType === DimensionType.SUPPLIER) {
    itemIdToName = supplierIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.SUPPLIER];
  } else if (dimensionType === DimensionType.DIVISION) {
    itemIdToName = divisionIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.DIVISION];
  } else if (dimensionType === DimensionType.RULE) {
    itemIdToName = ruleIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.RULE];
  } else if (dimensionType === DimensionType.AGREEMENT) {
    itemIdToName = agreementIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.AGREEMENT];
  } else if (dimensionType === DimensionType.RULE_TAG) {
    itemIdToName = ruleTagIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.RULE_TAG];
  } else if (dimensionType === DimensionType.AGREEMENT_TAG) {
    itemIdToName = agreementTagIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.AGREEMENT_TAG];
  } else if (dimensionType === DimensionType.SUPPLIER_TAG) {
    itemIdToName = supplierTagIdToName;
    groupingConfig = dimensionGroupingConfig?.[DimensionType.SUPPLIER_TAG];
  }

  if (!itemIdToName || itemIdToName.size === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!max-w-[95vw] sm:!max-w-[95vw] !w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              <T context="Dialog title for editing dimension grouping when no items are available">
                Edit <Var>{DIMENSION_LABELS[dimensionType]}</Var> Grouping
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="Dialog description when no dimension items are available for grouping">
                No <Var>{DIMENSION_LABELS[dimensionType].toLowerCase()}</Var>{' '}
                items available for grouping
              </T>
            </DialogDescription>
          </DialogHeader>
          <div className="py-8 text-center text-muted-foreground">
            <T context="Empty state message when there are no dimension items available to organize into groups">
              No <Var>{DIMENSION_LABELS[dimensionType].toLowerCase()}</Var>{' '}
              items available for grouping
            </T>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const defaultConfig: DimensionGroupingConfig = {
    userGroups: [],
    catchAllGroups: createDefaultCatchAllGroups(dimensionType),
  };

  const handleConfigChange = (config: DimensionGroupingConfig | undefined) => {
    if (onDimensionGroupingConfigChange) {
      onDimensionGroupingConfigChange(dimensionType, config);
    }
  };

  const handleConfirm = () => {
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const dimensionLabel = DIMENSION_LABELS[dimensionType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] sm:!max-w-[95vw] !w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            <T context="Dialog title for editing dimension grouping configuration">
              Edit <Var>{dimensionLabel}</Var> Grouping
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="Dialog description prompting user to organize dimension items into groups for analysis">
              Organize <Var>{dimensionLabel.toLowerCase()}</Var> items into
              groups for better data analysis
            </T>
          </DialogDescription>
        </DialogHeader>

        {/* Helpful Info Section */}
        <AlertContainer className="bg-muted/50 border-border mb-4">
          <InfoIcon className="h-4 w-4 text-muted-foreground" />
          <AlertDescription className="text-xs space-y-1.5">
            <p className="font-medium text-foreground">
              <T context="Heading for the informational section explaining how dimension grouping works">
                How grouping works:
              </T>
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>
                <T context="Explanation of user groups: custom groups where the user assigns dimension items, with examples like Tier 1 Suppliers or Strategic Partners">
                  <strong>User Groups:</strong> Create custom groups and assign{' '}
                  <Var>{dimensionLabel.toLowerCase()}</Var> items to them. For
                  example, group suppliers into &quot;Tier 1 Suppliers&quot; or
                  &quot;Strategic Partners&quot;.
                </T>
              </li>
              <li>
                <T context="Explanation of catch-all groups: automatically captures dimension items not matched by any user group, renameable but not deletable">
                  <strong>Catch-All Groups:</strong> Automatically includes all{' '}
                  <Var>{dimensionLabel.toLowerCase()}</Var> items that
                  don&apos;t match any user group. You can rename these groups
                  but cannot delete them.
                </T>
              </li>
              <li>
                <T context="Explanation of table impact: grouped view shows groups instead of individual dimension items for higher-level analysis">
                  <strong>Table Impact:</strong> When grouping is configured,
                  your table will show these groups instead of individual{' '}
                  <Var>{dimensionLabel.toLowerCase()}</Var> items, making it
                  easier to analyze data at a higher level.
                </T>
              </li>
            </ul>
          </AlertDescription>
        </AlertContainer>

        <div className="flex-1 overflow-y-auto min-h-0">
          <DimensionGroupingEditor
            dimensionType={dimensionType}
            itemIdToName={itemIdToName}
            groupingConfig={groupingConfig || defaultConfig}
            onConfigChange={handleConfigChange}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
